import { fetchQuery, graphql } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import type { LookupUsers } from '../../common/types/lookup';
import { ErrorWithStatusCode } from '../../common/utils/errors/error-with-status-code';
import type {
	fetchUsersQuery,
	fetchUsersQuery$data,
} from './__generated__/fetchUsersQuery.graphql';

export const fetchUsers = async (
	environment: IEnvironment,
	accountIds: string[],
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<LookupUsers> => {
	try {
		const apiStartTime = performance.now();
		const response: fetchUsersQuery$data | undefined = await fetchQuery<fetchUsersQuery>(
			environment,
			/* eslint-disable @atlassian/relay/unused-fields */
			graphql`
				query fetchUsersQuery($accountIds: [ID!]!) {
					users(accountIds: $accountIds) {
						id
						accountStatus
						accountId
						name
						picture
					}
				}
			`,
			{ accountIds },
			{
				networkCacheConfig: { force: false },
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		if (!response?.users) {
			throw new ErrorWithStatusCode(
				'[fetch-users service] [fetchUsersQuery]: Property users does not exist.',
				{
					cause: 'FETCH_USERS_MISSING_USERS_PROPERTY',
				},
				'FETCH_USERS_MISSING_USERS_PROPERTY',
			);
		}

		const apiLatencyMs = Math.floor(performance.now() - apiStartTime);

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModificationsFetchUsers',
			attributes: {
				usersAPILatency: apiLatencyMs,
			},
		});

		return [...response.users];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		if (!err.cause) {
			err.cause = 'FETCH_USERS_UNKNOWN_ERROR';
		}

		throw err;
	}
};
